/**
 * Shell bootstrap wire format (HS2-TMF3KZ), shared by the server renderer and the
 * SPA. Zod-free on purpose: the client guards the embedded block with the
 * structural check below and falls back to the normal fetches when it fails.
 */
import type { ShellBootstrap } from './contract/bootstrap.js';

/** The id of the `<script type="application/json">` element holding the bootstrap. */
export const SHELL_BOOTSTRAP_ID = 'shell-bootstrap';
/** The bootstrap shape version this build reads and writes. */
export const SHELL_BOOTSTRAP_VERSION = 2;
/** A bootstrap older than this (or this far in the future: clock skew) is ignored. */
export const SHELL_BOOTSTRAP_MAX_AGE_MS = 10 * 60_000;

/**
 * Serialize `bootstrap` for embedding in an HTML `<script>` element. JSON may hold
 * `</script>` or `<!--` inside a string (a demand title, say), which would end or
 * corrupt the element; escaping `<`, `>` and `&` as `<`-style sequences keeps
 * the text inert to the HTML parser while `JSON.parse` reads the same value. U+2028
 * and U+2029 are escaped too so the text stays valid if ever evaluated as script.
 */
export function serializeShellBootstrap(bootstrap: ShellBootstrap): string {
  return JSON.stringify(bootstrap)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const isStringOrNull = (value: unknown): boolean => value === null || typeof value === 'string';
const isStringArray = (value: unknown): boolean =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

/** The parts of `me` the startup path reads; anything else malformed fails later as it would from the API. */
function isMe(value: unknown): boolean {
  return (
    isRecord(value) &&
    value['authenticated'] === true &&
    typeof value['email'] === 'string' &&
    isStringOrNull(value['organizationId']) &&
    isStringOrNull(value['memberId']) &&
    isStringArray(value['roles']) &&
    typeof value['registered'] === 'boolean' &&
    typeof value['suspended'] === 'boolean'
  );
}

function isWorkspace(value: unknown, me: Record<string, unknown>): boolean {
  if (!isRecord(value)) return false;
  const cursor = value['demandsCursor'];
  return (
    typeof value['organizationId'] === 'string' &&
    value['organizationId'] === me['organizationId'] &&
    Array.isArray(value['organizations']) &&
    value['organizations'].every((org) => isRecord(org) && typeof org['id'] === 'string') &&
    Array.isArray(value['demands']) &&
    value['demands'].every((demand) => isRecord(demand) && typeof demand['id'] === 'string') &&
    isStringOrNull(value['demandsNextCursor']) &&
    isRecord(value['demandsStatusCounts']) &&
    Object.values(value['demandsStatusCounts']).every((n) => typeof n === 'number') &&
    typeof cursor === 'number' &&
    Number.isSafeInteger(cursor) &&
    cursor >= 0
  );
}

/**
 * Parse the embedded bootstrap text, or null when it is absent, not JSON, of another
 * version, too old, or not shaped like a signed-in bootstrap. A workspace that does
 * not match `me`'s organization is dropped (the caller then fetches the lists).
 */
export function parseShellBootstrap(text: string | null | undefined, now = Date.now()): ShellBootstrap | null {
  if (text === null || text === undefined || text.trim() === '') return null;
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return null;
  }
  if (!isRecord(value) || value['version'] !== SHELL_BOOTSTRAP_VERSION) return null;
  const issuedAt = value['issuedAt'];
  if (typeof issuedAt !== 'number' || Math.abs(now - issuedAt) > SHELL_BOOTSTRAP_MAX_AGE_MS) return null;
  const me = value['me'];
  if (!isMe(me) || !isRecord(me)) return null;
  const workspace = value['workspace'];
  const keep = workspace !== undefined && isWorkspace(workspace, me);
  // The guard above checked every field the client reads; the rest is the API's own shape.
  const bootstrap = value as unknown as ShellBootstrap;
  if (keep) return bootstrap;
  return { version: bootstrap.version, issuedAt: bootstrap.issuedAt, me: bootstrap.me };
}
